"use client";

import { useMemo, useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";

interface LineStats {
  total: number;
  nonEmpty: number;
  blank: number;
  longest: number;
  shortest: number;
  average: number;
}

function computeLineStats(text: string): LineStats {
  if (text.length === 0) {
    return {
      total: 0,
      nonEmpty: 0,
      blank: 0,
      longest: 0,
      shortest: 0,
      average: 0,
    };
  }
  const lines = text.split("\n");
  let nonEmpty = 0;
  let longest = 0;
  let shortest = Number.POSITIVE_INFINITY;
  let totalLength = 0;
  for (const line of lines) {
    if (line.trim().length === 0) {
      continue;
    }
    nonEmpty += 1;
    totalLength += line.length;
    longest = Math.max(longest, line.length);
    shortest = Math.min(shortest, line.length);
  }
  if (nonEmpty === 0) {
    shortest = 0;
  }
  const average = nonEmpty === 0 ? 0 : totalLength / nonEmpty;
  return {
    total: lines.length,
    nonEmpty,
    blank: lines.length - nonEmpty,
    longest,
    shortest,
    average,
  };
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const LINE_COUNTER_STRINGS = {
  yourText: "Your text",
  placeholder: "First line\nSecond line\n\nFourth line",
  totalLines: "Total lines",
  nonEmptyLines: "Non-empty lines",
  blankLines: "Blank lines",
  longestLineChars: "Longest line (chars)",
  shortestLineChars: "Shortest line (chars)",
  averageLineLength: "Average line length",
} as const;

export default function LineCounterWidget() {
  const [input, setInput] = useState("");
  const stats = useMemo(() => computeLineStats(input), [input]);
  const strings = useToolUi(LINE_COUNTER_STRINGS);
  const fmt = useToolNumber();

  return (
    <>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="line-input"
        >
          {strings.yourText}
        </label>
        <textarea
          className="min-h-[240px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="line-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.placeholder}
          value={input}
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <StatCard label={strings.totalLines} value={fmt(stats.total)} />
        <StatCard label={strings.nonEmptyLines} value={fmt(stats.nonEmpty)} />
        <StatCard label={strings.blankLines} value={fmt(stats.blank)} />
        <StatCard label={strings.longestLineChars} value={fmt(stats.longest)} />
        <StatCard
          label={strings.shortestLineChars}
          value={fmt(stats.shortest)}
        />
        <StatCard
          label={strings.averageLineLength}
          value={fmt(stats.average, {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          })}
        />
      </div>
    </>
  );
}
